"use client";

import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { useEffect } from "react";
import { useSession } from "next-auth/react";
import { useCookieConsent } from "react-cookie-manager";

// Track whether PostHog has been initialized
let isPostHogInitialized = false;

function initializePostHog() {
  if (typeof window === "undefined" || isPostHogInitialized) return;

  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
    api_host: "/hermes",
    ui_host: "https://eu.posthog.com",
    person_profiles: "identified_only",
    capture_pageview: true,
    capture_pageleave: true,
    capture_exceptions: true,
    debug: process.env.NODE_ENV === "development",
  });

  isPostHogInitialized = true;
}

function shutdownPostHog() {
  if (!isPostHogInitialized) return;

  // Opt out and clear all PostHog data
  posthog.opt_out_capturing();
  posthog.reset();
  isPostHogInitialized = false;
}

function PostHogAuthWrapper({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession();
  const { detailedConsent } = useCookieConsent();

  // Derive analytics consent from detailedConsent:
  // If detailedConsent is undefined, user is outside GDPR region - enable analytics by default
  // If detailedConsent exists, respect the user's explicit consent choice
  const hasConsent =
    detailedConsent === undefined ? true : (detailedConsent?.Analytics?.consented ?? false);

  // Initialize/shutdown PostHog based on consent
  useEffect(() => {
    if (hasConsent && !isPostHogInitialized) {
      initializePostHog();
    } else if (!hasConsent && isPostHogInitialized) {
      shutdownPostHog();
    }
  }, [hasConsent]);

  // Identify user in PostHog when session changes (only if consent is granted)
  useEffect(() => {
    if (status === "loading" || !hasConsent || !isPostHogInitialized) return;

    if (session?.user) {
      // Identify user in PostHog
      posthog.identify(session.user.id, {
        email: session.user.email,
        name: session.user.profileName ?? session.user.name,
        subscriptionTier: session.user.subscriptionTier,
        isAdmin: session.user.isAdmin,
      });
    } else if (status === "unauthenticated") {
      // Reset PostHog when user logs out
      posthog.reset();
    }
  }, [session, status, hasConsent]);

  return <>{children}</>;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  return (
    <PHProvider client={posthog}>
      <PostHogAuthWrapper>{children}</PostHogAuthWrapper>
    </PHProvider>
  );
}
